import type { CountSpecMisc } from "../../Processing/types/builderMisc";
import type { TableComponentSpec, TableSortConfig, MetricOption, SubjectEntity } from "../../Processing/types";
import type { GroupByOption } from "../constants";
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import type { PlayersFormFields } from "../shared/PlayersForm";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";

// ── State ──

export interface TableColumnFormState {
  /** Which items a count metric tallies. Absent for every measured metric. */
  countSpec?: CountSpecMisc;
  id: string;
  selectedMetricId: string | null;
  metricAgg: "avg" | "sum" | "best";
  enableColorOverride: boolean;
  colorOverride: string;
  showAcwr: boolean;
  customNameEnabled: boolean;
  customName: string;
}

export interface TableFormState extends DateRangeFormFields, PlayersFormFields {
  title: string;
  columns: TableColumnFormState[];
  showAverageRow: boolean;
  separateByPosition: boolean;
  excludeInjuredPlayers: boolean;
  expandedKey: string | string[];
  groupBy: GroupByOption;
  sortConfig?: TableSortConfig;
}

// ── Actions ──

export type TableAction =
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_SHOW_AVERAGE_ROW"; value: boolean }
  | { type: "SET_SEPARATE_BY_POSITION"; value: boolean }
  | { type: "SET_EXCLUDE_INJURED_PLAYERS"; value: boolean }
  | { type: "UPDATE_COLUMN"; index: number; patch: Partial<TableColumnFormState> }
  | { type: "ADD_COLUMN" }
  | { type: "REMOVE_COLUMN"; index: number }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] }
  | { type: "UPDATE_DATE_RANGE"; patch: Partial<DateRangeFormFields> }
  | { type: "UPDATE_PLAYERS"; patch: Partial<PlayersFormFields> }
  | { type: "SET_GROUP_BY"; groupBy: GroupByOption };

// ── Helpers ──

function createColumn(): TableColumnFormState {
  return {
    id: crypto.randomUUID(),
    selectedMetricId: null,
    metricAgg: "avg",
    enableColorOverride: false,
    colorOverride: "",
    showAcwr: false,
    customNameEnabled: false,
    customName: "",
  };
}

// ── Reducer ──

export function tableReducer(state: TableFormState, action: TableAction): TableFormState {
  switch (action.type) {
    case "SET_TITLE":
      return { ...state, title: action.title };

    case "SET_SHOW_AVERAGE_ROW":
      return { ...state, showAverageRow: action.value };

    case "SET_SEPARATE_BY_POSITION":
      return { ...state, separateByPosition: action.value };

    case "SET_EXCLUDE_INJURED_PLAYERS":
      return { ...state, excludeInjuredPlayers: action.value };

    case "UPDATE_COLUMN": {
      const becomingACount =
        action.patch.countSpec != null && state.columns[action.index]?.countSpec == null;
      return {
        ...state,
        // Disabling the Drill chip does not move a selection already sitting on it, and
        // the backend rejects a drill group-by on a count — none of the counted tables
        // carries a drill relation. Fall back to the default grain.
        groupBy: becomingACount && state.groupBy === "drill" ? "day" : state.groupBy,
        // A drill subject is silently dropped for a count; clear it with the group-by.
        selectedDrills: becomingACount ? [] : state.selectedDrills,
        columns: state.columns.map((c, i) =>
          i === action.index ? { ...c, ...action.patch } : c,
        ),
      };
    }

    case "ADD_COLUMN": {
      const col = createColumn();
      return {
        ...state,
        columns: [...state.columns, col],
        expandedKey: [col.id],
      };
    }

    case "REMOVE_COLUMN":
      if (state.columns.length <= 1) return state;
      return {
        ...state,
        columns: state.columns.filter((_, i) => i !== action.index),
      };

    case "SET_EXPANDED_KEY":
      return { ...state, expandedKey: action.key };

    case "UPDATE_DATE_RANGE":
      return { ...state, ...action.patch };

    case "UPDATE_PLAYERS":
      return { ...state, ...action.patch };

    case "SET_GROUP_BY":
      return { ...state, groupBy: action.groupBy };

    default:
      return state;
  }
}

// ── Factories ──

export function createInitialState(): TableFormState {
  const col = createColumn();
  return {
    title: "",
    columns: [col],
    showAverageRow: false,
    separateByPosition: false,
    excludeInjuredPlayers: true,
    expandedKey: [col.id],
    groupBy: "day",
    ...DATE_RANGE_DEFAULTS,
    ...PLAYERS_DEFAULTS,
  };
}

export function hydrateFromSpec(spec: TableComponentSpec): TableFormState {
  const misc = spec.table_builder_misc;
  const columnMetrics = misc?.columnMetrics ?? {};

  const columns: TableColumnFormState[] =
    spec.series.length > 0
      ? spec.series.map((s) => ({
          id: s.queryId || crypto.randomUUID(),
          selectedMetricId: columnMetrics[s.queryId] ?? null,
          countSpec: misc?.countSpecs?.[s.queryId],
          metricAgg: s.metricAgg,
          enableColorOverride: s.plotMutations?.colorOverride ?? false,
          colorOverride: s.colorOverride ?? "",
          showAcwr: s.metricIdentifierType === "acwr",
          customNameEnabled: !!s.displayNameOverride,
          customName: s.displayNameOverride ?? "",
        }))
      : [createColumn()];

  return {
    title: spec.display.title ?? "",
    columns,
    showAverageRow: spec.mutations.showAverageRow ?? false,
    separateByPosition: spec.mutations.separateByPosition ?? false,
    sortConfig: spec.mutations.sortConfig,
    excludeInjuredPlayers: misc?.excludeInjuredPlayers ?? true,
    expandedKey: columns.length > 0 ? [columns[0].id] : [],
    groupBy: (misc?.groupBy as GroupByOption) ?? "day",
    dateRange: misc?.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
    showAdvancedDate: misc?.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
    startDateType: misc?.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
    startDateLatestN: misc?.startDateLatestN,
    startDateAbsolute: misc?.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
    startDateOffset: misc?.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
    endDateType: misc?.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
    endDateAbsolute: misc?.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
    endDateOffset: misc?.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
    dayFilter: misc?.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
    dateControllerId: misc?.dateControllerId,
    subjectControllerId: misc?.subjectControllerId,
    subjectControllerMode: misc?.subjectControllerMode,
    selectedSubjects: misc?.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
    selectedDrills: misc?.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
    subjectActiveTab: misc?.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
  };
}

export function formStateToSpec(
  state: TableFormState,
  metrics: MetricOption[],
  getSubjectEntities?: (subjectType: string) => SubjectEntity[],
): TableComponentSpec | null {
  const configured = state.columns.filter((c) => c.selectedMetricId != null);
  const series = configured.map((c) => {
    const metric = metrics.find((m) => m.id === c.selectedMetricId);
    const isAcwr = c.showAcwr && c.selectedMetricId?.startsWith("gps-");
    return {
      queryId: c.id,
      displayName: isAcwr ? `${metric?.name ?? "Column"} (ACWR)` : metric?.name ?? "Column",
      ...(c.customName.trim() && { displayNameOverride: c.customName.trim() }),
      metricIdentifierType: (isAcwr ? "acwr" : "metric") as "metric" | "acwr",
      metricAgg: c.metricAgg,
      plotMutations: { colorOverride: c.enableColorOverride },
      ...(c.enableColorOverride && c.colorOverride && { colorOverride: c.colorOverride }),
    };
  });

  const selectedSubjectNames = getSubjectEntities
    ? resolveSubjectNames(state.selectedSubjects, state.selectedDrills, getSubjectEntities)
    : [];

  return {
    type: "table",
    display: { title: state.title || "" },
    series,
    mutations: {
      showAverageRow: state.showAverageRow,
      ...(state.separateByPosition && { separateByPosition: true }),
      ...(state.sortConfig && { sortConfig: state.sortConfig }),
    },
    table_builder_misc: {
      groupBy: state.groupBy,
      dateRange: state.dateRange,
      showAdvancedDate: state.showAdvancedDate,
      startDateType: state.startDateType,
      startDateLatestN: state.startDateLatestN,
      startDateAbsolute: state.startDateAbsolute,
      startDateOffset: state.startDateOffset,
      endDateType: state.endDateType,
      endDateAbsolute: state.endDateAbsolute,
      endDateOffset: state.endDateOffset,
      dayFilter: state.dayFilter,
      dateControllerId: state.dateControllerId,
      subjectControllerId: state.subjectControllerId,
      subjectControllerMode: state.subjectControllerMode,
      selectedSubjects: state.selectedSubjects,
      selectedDrills: state.selectedDrills,
      subjectActiveTab: state.subjectActiveTab,
      selectedSubjectNames,
      excludeInjuredPlayers: state.excludeInjuredPlayers,
      columnMetrics: Object.fromEntries(
        configured.map((c) => [c.id, c.selectedMetricId]),
      ),
      countSpecs: Object.fromEntries(
        configured.filter((c) => c.countSpec).map((c) => [c.id, c.countSpec!]),
      ),
    },
  };
}
